import {
  SINGLE_SELECT,
  MULTI_SELECT,
  TEXT,
  CONSTANT,
  DATE,
  FORMAT_CONTROLLED,
  PARENT_FIELD,
  DISABLED_CELL_STYLE,
  ENABLED_CELL_STYLE
} from './constants';

import TextCellEditor from './TextCellEditor';
import MultiValueTextCellEditor from './MultiValueTextCellEditor';
import DropdownCellEditor from './DropdownCellEditor';
import TextCellRenderer from './TextCellRenderer';
import MultiValueTextCellRenderer from './MultiValueTextCellRenderer';
import SingleSelectCellRenderer from './SingleSelectCellRenderer';
import MultiSelectCellRenderer from './MultiSelectCellRenderer';
import DateCellRenderer from './DateCellRenderer';
import DateCellEditor from './DateCellEditor';
import { calculatedFormatValue } from '../conventionFields/fieldHelpers';
import PinnedRowActionCellRenderer from './PinnedRowActionCellRenderer';

// Formats cell values for display - handles objects, arrays, and primitive values
export const formatValue = (params) => {
  if (!params.value) return '';

  if (typeof params.value === 'object') {
    if (Array.isArray(params.value)) {
      return params.value.map(item =>
        typeof item === 'object' ? (item.value || '') : item
      ).join(', ');
    }
    return params.value.value || '';
  }

  return params.value;
};

// Parses string input values into object format for grid cells
export const parseValue = (params) => {
  if (typeof params.newValue === 'string') {
    return { value: params.newValue };
  }
  return params.newValue;
};

// Parses comma-separated strings into array of value objects
export const parseMultiValue = (params, column) => {
  if (typeof params.newValue === 'string') {
    const values = params.newValue.split(',').map(v => v.trim()).filter(v => v);
    return values.map(v => ({ value: v }));
  }
  return params.newValue;
};

// Parses single select input, matching against column options
export const parseSelectValue = (params, column) => {
  if (typeof params.newValue === 'string') {
    const option = column.values.find(opt => opt.value === params.newValue);
    return option || { value: params.newValue };
  }
  return params.newValue;
};

// Parses multi-select input, matching comma-separated values against column options
export const parseMultiSelectValue = (params, column) => {
  if (typeof params.newValue === 'string') {
    const values = params.newValue.split(',').map(v => v.trim()).filter(v => v);
    return values.map(v => {
      const option = column.values.find(opt => opt.value === v);
      return option || { value: v };
    });
  }
  return params.newValue;
};

// Transforms option objects into dropdown component format
export const selectValues = (values) => {
  return values.map((option) => ({
    value: option.value,
    label: option.value,
    description: option.description,
    color: option.color
  }));
};

export const columnTypes = {
  selectColumn: {
    cellEditorPopup: true,
    cellEditor: DropdownCellEditor,
    suppressKeyboardEvent: (params) => params.editing,
  }
};

export const getStaticColumnDefs = () => ([
  {
    headerName: '',
    field: 'row_action',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    resizable: false,
    floatingFilter: false,
    suppressSizeToFit: true,
    width: 40,
    minWidth: 40,
    maxWidth: 40,
    tooltipValueGetter: (params) => {
      if (params.node.rowPinned) {
        return "Specify values in this row and click Generate button to populate with all possible combinations.";
      }
      return '';
    },
    cellRendererSelector: (params) => {
      if (params.node.rowPinned) {
        return {
          component: PinnedRowActionCellRenderer
        };
      }
    },
  }
]);

export const searchColumnCustomData = (columnsCustomDataMap, field) => {
  return columnsCustomDataMap.get(field) || null;
};

const getCommonColumnDef = (column) => ({
  headerName: column.header_name,
  field: column.field,
  editable: true,
  cellStyle: ENABLED_CELL_STYLE,
  headerComponentParams: {
    required: column.required,
    description: column.description,
    typeName: column.column_type_name,
  },
  tooltipValueGetter: (params) => {
    if (params.value) {
      switch (typeof (params.value)) {
        case 'string':
          return params.value;
        case 'object':
          if (Array.isArray(params.value)) {
            return params.value.map((item) => item.value).join(', ');
          } else {
            return params.value.description || params.value.value;
          }
        default:
          return '';
      }
    }

    return '';
  },
  hide: !column.visible
});

export const getFormatControlledValue = (params, activeFieldColumns) => {
  let formatFields = [];
  const customData = searchColumnCustomData(params.context.columnsCustomData, params.column.colId);
  for (const fieldId of customData.field_ids) {
    const field = `field_${fieldId}`;
    const column = activeFieldColumns[field];
    if (column) {
      const fieldCustomData = searchColumnCustomData(params.context.columnsCustomData, field);
      const cellValueObject = params.api.getCellValue({ rowNode: params.node, colKey: column.field }) || { value: '' };
      const fieldData = {
        id: fieldCustomData.id,
        name: fieldCustomData.name,
        current_value: cellValueObject.value,
        default_value: fieldCustomData.default_value,
        prefix: fieldCustomData.prefix,
        suffix: fieldCustomData.suffix
      };
      formatFields.push(fieldData);
    }
  }
  const result = calculatedFormatValue(formatFields, customData.separator);
  return { ...result, formatId: customData.format_id };
};

export const getColumnDef = (column) => {
  const commonDef = getCommonColumnDef(column);
  const textCellEditorParams = {
    textLimit: column.text_limit,
    allowedCharacters: column.allowed_characters,
    prohibitedCharacters: column.prohibited_characters,
  };

  switch (column.column_type) {
    case TEXT:
      return {
        ...commonDef,
        cellEditorSelector: params => {
          if (params.node.rowPinned && column.allow_combo_generate) {
            return {
              component: MultiValueTextCellEditor,
              params: { ...textCellEditorParams }
            };
          }
          return {
            component: TextCellEditor,
            params: textCellEditorParams
          };
        },
        cellEditorPopup: true,
        suppressKeyboardEvent: (params) => params.editing,
        cellRendererSelector: (params) => {
          if (params.node.rowPinned && column.allow_combo_generate) {
            return { component: MultiValueTextCellRenderer };
          }
          return { component: TextCellRenderer, params: { prefix: column.prefix, suffix: column.suffix } };
        },
        valueFormatter: formatValue,
        valueParser: (params) => {
          if (params.node.rowPinned && column.allow_combo_generate) {
            return parseMultiValue(params, column);
          }
          return parseValue(params);
        },
        // Allow paste for URL and description (notes) columns
        suppressPaste: column.field !== 'url' && column.field !== 'description'
      };

    case SINGLE_SELECT:
      return {
        ...commonDef,
        type: ['selectColumn'],
        cellRendererSelector: (params) => {
          if (params.node.rowPinned && column.allow_combo_generate) {
            return { component: MultiSelectCellRenderer };
          }
          return { component: SingleSelectCellRenderer };
        },
        cellEditorSelector: params => ({
          component: DropdownCellEditor,
          params: {
            allowCreate: column.allow_create,
            multipleSelect: params.node.rowPinned && column.allow_combo_generate,
            options: selectValues(column.values),
            ...textCellEditorParams
          }
        }),
        valueFormatter: formatValue,
        valueParser: (params) => {
          if (params.node.rowPinned && column.allow_combo_generate) {
            return parseMultiSelectValue(params, column);
          }
          return parseSelectValue(params, column);
        },
        suppressPaste: false,  // Allow paste for single select columns
        // Validate pasted values against allowed options
        processCellFromClipboard: (params) => {
          if (typeof params.value === 'string' && column.values) {
            const option = column.values.find(opt => opt.value === params.value.trim());
            return option || params.node.data[params.column.colId];
          }
          return params.value;
        }
      };

    case MULTI_SELECT:
      return {
        ...commonDef,
        type: ['selectColumn'],
        cellRenderer: MultiSelectCellRenderer,
        cellEditorParams: {
          allowCreate: column.allow_create,
          multipleSelect: true,
          options: selectValues(column.values)
        },
        valueFormatter: formatValue,
        valueParser: (params) => parseMultiSelectValue(params, column)
      };

    case CONSTANT:
      return {
        ...commonDef,
        editable: false,
        cellStyle: DISABLED_CELL_STYLE,
        valueFormatter: formatValue,
        valueParser: parseValue
      };

    case DATE:
      return {
        ...commonDef,
        suppressKeyboardEvent: (params) => params.editing,
        cellRenderer: DateCellRenderer,
        cellEditor: DateCellEditor,
        cellEditorParams: {
          dateFormat: column.date_format,
          portalId: 'grid-mode-url-builder'
        },
        valueSetter: (params) => {
          params.data[params.colDef.field] = params.newValue;
          return true;
        },
        valueFormatter: formatValue,
        valueParser: parseValue
      };

    case FORMAT_CONTROLLED:
      return {
        ...commonDef,
        editable: false,
        cellStyle: DISABLED_CELL_STYLE,
        valueGetter: (params) => {
          if (params.node.rowPinned) {
            return { value: '' };
          }
          return params.context.getFormatControlledValue(params);
        },
        valueFormatter: formatValue,
        valueParser: parseValue
      };

    case PARENT_FIELD:
      return {
        ...commonDef,
        type: ['selectColumn'],
        editable: (params) => params.node.rowPinned,
        cellStyle: (params) => params.node.rowPinned ? ENABLED_CELL_STYLE : DISABLED_CELL_STYLE,
        cellRendererSelector: (params) => {
          if (params.node.rowPinned) {
            return { component: SingleSelectCellRenderer };
          }
          return { params: { style: DISABLED_CELL_STYLE } };
        },
        valueGetter: (params) => {
          if (params.node.rowPinned) {
            return params.data[params.colDef.field];
          }
          return params.api.getPinnedTopRow(0).data[params.colDef.field];
        },
        valueFormatter: formatValue,
        valueParser: (params) => parseSelectValue(params, column),
        cellEditorParams: {
          allowCreate: false,
          multipleSelect: false,
          options: selectValues(column.values)
        }
      };

    default:
      return commonDef;
  }
};